import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { apiClient, useAuth } from '../auth-lib';
import { PageLoader } from '../design-system';
import './Landing.css';

// The public marketing page.
//
// ⚠️ EVERY WORD ON THIS PAGE COMES FROM THE DATABASE, not from this file. The
// super admin writes it at /admin/landing; this component only lays it out.
// That is why there is no hard-coded headline, no sample testimonial and no
// invented customer count anywhere below: a trust signal on a marketing page is
// a claim the platform owner is personally making, and shipping a plausible
// fake one — even as a placeholder — is how a fabricated endorsement reaches
// production. An empty list hides its section. Nothing invents itself.
//
// Rendered at `/` for a signed-out visitor. A signed-in one is sent straight
// into the app: somebody with a session wants their work, not the sales pitch.

// The icon set a section may name. Kept here as a closed set rather than
// letting content supply a URL — a content edit must never be able to point the
// page at a third-party asset.
const ICONS = {
  spark: 'M12 2l2.2 6.3L20.5 10l-5.3 3.9L17 20.5 12 16.8 7 20.5l1.8-6.6L3.5 10l6.3-1.7z',
  calendar: 'M7 3v3m10-3v3M3.5 9h17M5 5.5h14a1.5 1.5 0 011.5 1.5v12A1.5 1.5 0 0119 20.5H5A1.5 1.5 0 013.5 19V7A1.5 1.5 0 015 5.5z',
  shield: 'M12 3l7 3v5.5c0 4.3-2.9 8.1-7 9.5-4.1-1.4-7-5.2-7-9.5V6z',
  invoice: 'M6 3.5h12v17l-3-2-3 2-3-2-3 2zM9 8.5h6M9 12.5h6',
  chart: 'M4 20V10m5 10V5m5 15v-7m5 7V8',
  people: 'M8.5 11a3 3 0 100-6 3 3 0 000 6zm7.5 0a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM3 19.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5M16 14.8c2.4.3 4 2.1 4 4.7',
  lock: 'M7 10.5V8a5 5 0 0110 0v2.5M5.5 10.5h13v9h-13z',
  bolt: 'M13 2.5L5 13.5h6l-1 8 8-11h-6z',
};

const Icon = ({ name }) => (
  <svg className="ld-ico" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d={ICONS[name] || ICONS.spark} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── The hero illustration ────────────────────────────────────────────────────
//
// A signed-out visitor previously met a wall of centred text with nothing to
// look at, which is why the page read as empty however much copy went into it.
//
// ⚠️ THIS IS AN ILLUSTRATION, NOT A SCREENSHOT, and the distinction is why the
// rows below carry no client names. A mocked-up product shot invents whatever it
// shows — a customer, a number, a name — and it is indistinguishable from a real
// one to the person looking at it. What is drawn here instead is the ONE unit
// this product actually deals in: a statutory head, the period it covers, the
// date it is due and the state it is in. Every head, cadence and due date below
// is real (`src/seed/complianceTypes.js`), so the picture is a description of
// the product rather than a claim about anybody using it.
//
// `aria-hidden` throughout: it is decoration that restates the headline beside
// it, and reading four table rows of sample dates aloud helps nobody.
const CALENDAR_ROWS = [
  { head: 'GSTR-1', period: 'Jul 2026', due: '11 Aug', state: 'Filed', tone: 'done' },
  { head: 'GSTR-3B', period: 'Jul 2026', due: '20 Aug', state: 'In review', tone: 'live' },
  { head: 'TDS 26Q', period: 'Q1 FY26-27', due: '31 Jul', state: 'Overdue', tone: 'late' },
  { head: 'AOC-4', period: 'FY 2025-26', due: '29 Oct', state: 'Upcoming', tone: 'idle' },
];

const HeroVisual = ({ brandName }) => (
  <div className="ld-shot" aria-hidden="true">
    <div className="ld-shot-bar">
      <span className="ld-shot-dot" />
      <span className="ld-shot-dot" />
      <span className="ld-shot-dot" />
      <span className="ld-shot-addr">{brandName}</span>
    </div>
    <div className="ld-shot-body">
      <div className="ld-shot-head">
        <span className="ld-shot-title">Compliance calendar</span>
        <span className="ld-shot-chip">This month</span>
      </div>
      <ul className="ld-shot-rows">
        {CALENDAR_ROWS.map((r) => (
          <li className="ld-shot-row" key={r.head + r.period}>
            <span className="ld-shot-head-name">{r.head}</span>
            <span className="ld-shot-period">{r.period}</span>
            <span className="ld-shot-due">{r.due}</span>
            <span className={`ld-shot-state is-${r.tone}`}>{r.state}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

// A CTA the content may leave blank — an empty label means "don't render it",
// which is how "this page has one button, not two" gets expressed without a
// separate flag.
//
// Rendered as a real <a>/<Link> styled from the design tokens rather than
// wrapping the <Button> component: Button renders a <button>, and a navigation
// target inside one is neither middle-clickable nor copyable as a link. An
// external href also skips the router and drops `window.opener`.
const LinkButton = ({ cta, variant = 'primary' }) => {
  if (!cta?.label) return null;
  const href = cta.href || '/register';
  if (/^https?:\/\//i.test(href)) {
    return <a className={`ld-btn ld-btn-${variant}`} href={href} target="_blank" rel="noopener noreferrer">{cta.label}</a>;
  }
  return <Link className={`ld-btn ld-btn-${variant}`} to={href}>{cta.label}</Link>;
};

// ── The three shapes a section can take ──────────────────────────────────────
//
// ⚠️ THE SHAPE IS CHOSEN BY `key`, not by a field on the content. The layout is
// code — the same reason `SECTION_KEYS` is a closed set on the server — and a
// "pick your layout" dropdown in the editor would let somebody render the pain
// list as cheerful cards, which is the one thing it must never look like.
//
// All three read the same `features[]`, so the editor stays one form.

// Cards. The default, and what eight of the eleven sections use.
const FeatureCards = ({ features }) => (
  <div className="ld-features" data-count={features.length}>
    {features.map((f) => (
      <article className="ld-feature" key={f._id || f.title}>
        <span className="ld-feature-ico"><Icon name={f.icon} /></span>
        <h3>{f.title}</h3>
        <p>{f.body}</p>
      </article>
    ))}
  </div>
);

// The problem. Struck rows rather than cards, because the reader is meant to
// recognise these and want them gone — drawing them as tidy feature cards with
// a proud accent icon says the opposite of what the words say.
const PainList = ({ features }) => (
  <ul className="ld-pains">
    {features.map((f) => (
      <li className="ld-pain" key={f._id || f.title}>
        <span className="ld-pain-mark" aria-hidden="true" />
        <div>
          <h3>{f.title}</h3>
          <p>{f.body}</p>
        </div>
      </li>
    ))}
  </ul>
);

// How it works. Numbered and connected, because the whole claim is that these
// happen in order and mostly without you.
const StepList = ({ features }) => (
  <ol className="ld-steps">
    {features.map((f, i) => (
      <li className="ld-step" key={f._id || f.title}>
        <span className="ld-step-n" aria-hidden="true">{i + 1}</span>
        <h3>{f.title}</h3>
        <p>{f.body}</p>
      </li>
    ))}
  </ol>
);

const SectionBody = ({ section }) => {
  if (section.key === 'problem') return <PainList features={section.features} />;
  if (section.key === 'steps') return <StepList features={section.features} />;
  return <FeatureCards features={section.features} />;
};

function Landing() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [content, setContent] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | none

  useEffect(() => {
    apiClient
      .get('/api/landing')
      .then((r) => { setContent(r.data); setState('ready'); })
      // 404 is the normal answer when nothing is published yet — not an error
      // to shout about, just "there is no marketing page".
      .catch(() => setState('none'));
  }, []);

  // The page title is content-controlled, so it is set here rather than in
  // index.html. Restored on unmount so navigating into the app doesn't keep the
  // marketing title in the tab.
  useEffect(() => {
    if (state !== 'ready') return undefined;
    const previous = document.title;
    const t = content?.seo?.metaTitle || content?.brandName;
    if (t) document.title = t;
    return () => { document.title = previous; };
  }, [state, content]);

  // Somebody with a session wants their work, not the pitch.
  if (!authLoading && isAuthenticated) return <Navigate to="/org-admin" replace />;
  if (authLoading || state === 'loading') return <PageLoader />;
  // Nothing published: fall through to what `/` did before this page existed.
  if (state === 'none') return <Navigate to="/login" replace />;

  const c = content;
  const has = (a) => Array.isArray(a) && a.length > 0;

  return (
    <div className="ld">
      <header className="ld-nav">
        <div className="ld-wrap ld-nav-inner">
          <span className="ld-brand">{c.brandName}</span>
          <nav className="ld-nav-links" aria-label="Sections">
            {has(c.sections) && <a href="#features">Features</a>}
            {has(c.pricing?.plans) && <a href="#pricing">Pricing</a>}
            {has(c.faq) && <a href="#faq">FAQ</a>}
          </nav>
          <div className="ld-nav-actions">
            <Link className="ld-btn ld-btn-ghost" to="/login">Sign in</Link>
            <LinkButton cta={c.hero?.primaryCta} />
          </div>
        </div>
      </header>

      <main>
        {/* ── Hero ── */}
        <section className="ld-hero">
          <div className="ld-wrap ld-hero-inner">
            {c.hero?.eyebrow && <p className="ld-eyebrow">{c.hero.eyebrow}</p>}
            {c.hero?.title && <h1 className="ld-h1">{c.hero.title}</h1>}
            {c.hero?.subtitle && <p className="ld-lede">{c.hero.subtitle}</p>}
            <div className="ld-cta-row">
              <LinkButton cta={c.hero?.primaryCta} />
              <LinkButton cta={c.hero?.secondaryCta} variant="ghost" />
            </div>
            {has(c.hero?.notes) && (
              <ul className="ld-notes">
                {c.hero.notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            )}
          </div>
          <HeroVisual brandName={c.brandName} />
        </section>

        {/* ── Stats. Typed by the platform owner, never computed from tenant
             data — see the note on the model. ── */}
        {has(c.stats) && (
          <section className="ld-stats">
            <div className="ld-wrap ld-stats-grid">
              {c.stats.map((s) => (
                <div className="ld-stat" key={s._id || s.label}>
                  <span className="ld-stat-value">{s.value}</span>
                  <span className="ld-stat-label">{s.label}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Supported portals. The one list NOT typed by hand: these are the
             real rows in the global portal catalog, i.e. what the product can
             actually sign into. Names only.

             ⚠️ SITS HERE, DIRECTLY UNDER THE STATS, and not further down where
             it used to. It is the answer to "will this fit the way we already
             work?", which is the objection a visitor raises before they will
             read a single feature — so it belongs before the argument, not
             after it. ── */}
        {has(c.portals) && (
          <section className="ld-portals">
            <div className="ld-wrap">
              <p className="ld-eyebrow ld-centre">Signs in to</p>
              <ul className="ld-portal-strip">
                {c.portals.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </div>
          </section>
        )}

        {/* ── Feature sections ── */}
        {has(c.sections) && (
          <div id="features">
            {c.sections.map((s, idx) => (
              <section className={`ld-section ${idx % 2 ? 'is-alt' : ''}`} key={s._id || s.key}>
                <div className="ld-wrap">
                  <div className="ld-section-head">
                    {s.eyebrow && <p className="ld-eyebrow">{s.eyebrow}</p>}
                    {s.title && <h2 className="ld-h2">{s.title}</h2>}
                    {s.body && <p className="ld-body">{s.body}</p>}
                  </div>
                  {has(s.features) && <SectionBody section={s} />}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* ── Testimonials ── */}
        {has(c.testimonials) && (
          <section className="ld-section is-alt">
            <div className="ld-wrap">
              <div className="ld-quotes">
                {c.testimonials.map((t) => (
                  <figure className="ld-quote" key={t._id || t.name}>
                    <blockquote>{t.quote}</blockquote>
                    <figcaption>
                      <strong>{t.name}</strong>
                      {(t.role || t.firm) && (
                        <span>{[t.role, t.firm].filter(Boolean).join(', ')}</span>
                      )}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Pricing ── */}
        {has(c.pricing?.plans) && (
          <section className="ld-section" id="pricing">
            <div className="ld-wrap">
              <div className="ld-section-head">
                {c.pricing.eyebrow && <p className="ld-eyebrow">{c.pricing.eyebrow}</p>}
                {c.pricing.title && <h2 className="ld-h2">{c.pricing.title}</h2>}
                {c.pricing.body && <p className="ld-body">{c.pricing.body}</p>}
              </div>
              <div className="ld-plans" data-count={c.pricing.plans.length}>
                {c.pricing.plans.map((p) => (
                  <article className={`ld-plan ${p.isFeatured ? 'is-featured' : ''}`} key={p._id || p.name}>
                    {p.isFeatured && <span className="ld-plan-tag">Most popular</span>}
                    <h3>{p.name}</h3>
                    <p className="ld-plan-price">
                      <strong>{p.price}</strong>
                      {p.period && <span>{p.period}</span>}
                    </p>
                    {p.blurb && <p className="ld-plan-blurb">{p.blurb}</p>}
                    {has(p.features) && (
                      <ul className="ld-plan-features">
                        {p.features.map((f, i) => <li key={i}>{f}</li>)}
                      </ul>
                    )}
                    <LinkButton
                      cta={{ label: p.ctaLabel, href: p.ctaHref }}
                      variant={p.isFeatured ? 'primary' : 'ghost'}
                    />
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── FAQ. <details> rather than JS state: it is keyboard-accessible
             and findable by in-page search for free. ── */}
        {has(c.faq) && (
          <section className="ld-section is-alt" id="faq">
            <div className="ld-wrap ld-faq-wrap">
              <h2 className="ld-h2 ld-centre">Questions</h2>
              <div className="ld-faq">
                {c.faq.map((f) => (
                  <details key={f._id || f.question}>
                    <summary>{f.question}</summary>
                    <p>{f.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Closing CTA ── */}
        {(c.closing?.title || c.closing?.cta?.label) && (
          <section className="ld-closing">
            <div className="ld-wrap ld-closing-inner">
              {c.closing.title && <h2 className="ld-h2">{c.closing.title}</h2>}
              {c.closing.body && <p className="ld-body">{c.closing.body}</p>}
              <div className="ld-cta-row ld-centre-row">
                <LinkButton cta={c.closing.cta} />
              </div>
            </div>
          </section>
        )}
      </main>

      <footer className="ld-footer">
        <div className="ld-wrap ld-footer-inner">
          <span>{c.brandName}{c.tagline ? ` — ${c.tagline}` : ''}</span>
          <span className="ld-footer-right">
            {c.contactEmail && <a href={`mailto:${c.contactEmail}`}>{c.contactEmail}</a>}
            <Link to="/login">Sign in</Link>
          </span>
        </div>
        {c.footerNote && <div className="ld-wrap ld-footer-note">{c.footerNote}</div>}
      </footer>
    </div>
  );
}

export default Landing;
